import type { WorkDocument, WorkRevision } from "@artemis-group/aiw-contracts";

export interface PendingWorkSave {
  idempotencyKey: string;
  expectedLatestRevisionId: string | null;
  sourceRevisionId: string | null;
  document: WorkDocument;
  queuedAt: string;
}

export interface InstallerWorkPack {
  workId: string;
  jobRef: string;
  userId: string;
  document: WorkDocument;
  latestOwnRevisionId: string | null;
  sourceRevisionId: string | null;
  dirty: boolean;
  pending: PendingWorkSave | null;
  cachedAt: string;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const packKey = (userId: string, workId: string) => `aiw.installer.work-pack.v1.${userId}.${workId}`;

export function readPack(storage: StorageLike, userId: string, workId: string): InstallerWorkPack | null {
  try {
    const raw = storage.getItem(packKey(userId, workId));
    if (!raw) return null;
    const value = JSON.parse(raw) as InstallerWorkPack;
    return value.workId === workId && value.userId === userId && value.document?.schema === "artemis.work-record/1" ? value : null;
  } catch { return null; }
}

export function writePack(storage: StorageLike, pack: InstallerWorkPack): InstallerWorkPack {
  storage.setItem(packKey(pack.userId, pack.workId), JSON.stringify(pack));
  return pack;
}

export function initialPack(args: {
  workId: string; jobRef: string; userId: string; revision: WorkRevision; latestOwnRevisionId: string | null; now: string;
}): InstallerWorkPack {
  return {
    workId: args.workId, jobRef: args.jobRef, userId: args.userId,
    document: structuredClone(args.revision.document), latestOwnRevisionId: args.latestOwnRevisionId,
    sourceRevisionId: args.revision.id, dirty: false, pending: null, cachedAt: args.now,
  };
}

export function changePack(pack: InstallerWorkPack, document: WorkDocument, now: string): InstallerWorkPack {
  return { ...pack, document: structuredClone(document), dirty: true, cachedAt: now };
}

export function queuePack(pack: InstallerWorkPack, idempotencyKey: string, now: string): InstallerWorkPack {
  if (pack.pending) return pack;
  return {
    ...pack,
    pending: {
      idempotencyKey,
      expectedLatestRevisionId: pack.latestOwnRevisionId,
      sourceRevisionId: pack.sourceRevisionId,
      document: structuredClone(pack.document),
      queuedAt: now,
    },
  };
}

export function completePending(pack: InstallerWorkPack, revisionId: string, now: string): InstallerWorkPack {
  if (!pack.pending) return pack;
  const unchangedSinceQueue = JSON.stringify(pack.document) === JSON.stringify(pack.pending.document);
  return {
    ...pack,
    latestOwnRevisionId: revisionId,
    sourceRevisionId: null,
    dirty: !unchangedSinceQueue,
    pending: null,
    cachedAt: now,
  };
}
